import { CertificationCertificateView } from '@/components/Certifications';
import { FEATURE_FLAGS, WORDS } from '@/constants';
import { formatDate } from '@/utils';
import { SkeletonLoader } from '@/components/Loaders';
import { SlideoutModal } from '@/components/Modals';
import { useGlobalStore } from '@/store';
import { useMemo } from 'react';

/**
 * Props for the SlideoutModalCertificate component.
 */
interface ISlideoutModalCertificateProps {
  /** The context in which the sidebar is being used, either 'Sales Representative' or 'Manager'. */
  context: 'Sales Representative' | 'Manager';
  /** Indicates if the sidebar is in a loading state. */
  isLoading?: boolean;
  /** Optional callback function to be called when the sidebar is closed. */
  onClose?: () => void;
}

/**
 * SlideoutModalCertificate Component
 *
 * This component renders a sidebar that displays the certificate for a certification. It includes
 * functionality for printing the certificate, navigating to the next certificate in the history,
 * and closing the sidebar.
 */
export const SlideoutModalCertificate: React.FC<ISlideoutModalCertificateProps> = ({
  context,
  isLoading,
  onClose,
}) => {
  const { resetActives, sidebarCertificate, setSidebarCertificate } = useGlobalStore();

  /**
   * Memoized values for certification, score, and simulation based on the
   * current sidebar certificate context.
   *
   * This ensures that the component only re-computes these values when the
   * relevant parts of the sidebar certificate context change.
   *
   * It also provides a clear structure for accessing these values throughout the component.
   */
  const [certification, score, simulation, userName] = useMemo(() => {
    return [
      sidebarCertificate?.certification || null,
      sidebarCertificate?.score || null,
      sidebarCertificate?.simulation || null,
      sidebarCertificate?.userName || null,
    ];
  }, [sidebarCertificate]);

  /**
   * Memoized value for the formatted certificate completion date.
   */
  const certificateDateCompleted = useMemo(() => {
    if (!certification?.certificationSimulation?.completedAt) {
      return '';
    }

    const date = new Date(certification.certificationSimulation.completedAt);
    return formatDate(date, 'monthYear');
  }, [certification]);

  /**
   * Handles closing the sidebar by resetting active selections and
   * clearing the sidebar certificate context.
   */
  const handleClose = () => {
    resetActives();
    setSidebarCertificate(null);
    if (onClose) onClose();
  };

  // ============================================================
  // Conditional Rendering Based on Feature Flag
  // ============================================================
  if (!FEATURE_FLAGS.ENABLE_FINAL_CERTIFICATION) {
    return null;
  }

  /**
   * Override component for the sidebar title.
   *
   * Displays the user's name and the date the score was completed for the manager context,
   * and just the date for the sales representative context. This provides a more personalized
   * and informative title area based on the context of the sidebar.
   */
  const TitleOverrideComponent = () => {
    // Class names for the title, applying consistent styling across contexts
    const classNames = [
      'font-text',
      'leading-section-subtitle',
      'text-neutral-gray-700',
      'text-section-subtitle',
      'whitespace-nowrap',
    ].join(' ');

    /**
     * Formats the date completed for display in the title. This is used in both contexts,
     * but the manager context includes the user's name for additional context.
     */
    const dateCompleted = certificateDateCompleted
      ? formatDate(certification?.certificationSimulation?.completedAt, 'fullDatetime')
      : '';

    // Render the title based on the context, including the user's name for the
    // manager context and just the date for the sales representative context
    if (context === 'Manager') {
      const ariaLabel = `${userName}'s ${WORDS.SCORECARD} completed on ${dateCompleted}`;

      if (isLoading) {
        return <SkeletonLoader height="sm" width="2xl" rounded="none" />;
      }

      return (
        <h2 aria-label={ariaLabel} className={classNames}>
          <span className="font-bold">{userName}</span>
          <span className="mx-3">{`|`}</span>
          <time dateTime={dateCompleted}>{dateCompleted}</time>
        </h2>
      );
    }

    // For the sales representative context, we only display the
    // date completed without the user's name
    if (isLoading) {
      return <SkeletonLoader height="sm" width="lg" rounded="none" />;
    }

    return (
      <h2 aria-label={`Completed on ${dateCompleted}`} className={classNames}>
        <time dateTime={dateCompleted}>{dateCompleted}</time>
      </h2>
    );
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <SlideoutModal
      closeButtonIconOnly={false}
      isOpen={sidebarCertificate !== null}
      onClose={handleClose}
      titleOverride={<TitleOverrideComponent />}
    >
      <div className="flex flex-col p-4 lg:p-10 2xl:px-14 2xl:py-20">
        <CertificationCertificateView
          recipientName={userName || ''}
          dateCompleted={certificateDateCompleted}
          showHeader={false}
          showIntroText={false}
        />
      </div>
    </SlideoutModal>
  );
};

SlideoutModalCertificate.displayName = 'SlideoutModalCertificate';

export default SlideoutModalCertificate;
